import {Component, inject, OnDestroy, QueryList, TemplateRef, ViewChild, ViewChildren, ChangeDetectionStrategy} from '@angular/core';
import {SortableDirective, SortEvent} from "@app/_directives/sortable.directive";
import {Customer, RegenerateExtractRequest, RegenerateExtractResponse, VineMedsS3ObjectVersion} from "@app/_models/api";
import {BehaviorSubject, Observable, Subscription} from "rxjs";
import {GlobalService} from "@app/_services/global.service";
import {ApiService} from "@app/_services/api.service";
import {AsyncPipe, formatDate} from "@angular/common";
import {ExtractsService} from "@app/extracts/extracts.service";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {
    NgbCalendar,
    NgbDate,
    NgbInputDatepicker,
    NgbModal,
    NgbModalRef
} from "@ng-bootstrap/ng-bootstrap";
import {filesize} from "filesize";
import {ActivatedRoute} from "@angular/router";
import {ModalConfirmComponent} from "@app/_components/modal-confirm/modal-confirm.component";
import {DaysOfWeek} from "@app/_models/global";
import {ToastService} from "@app/_services/toast.service";
import {TablePagerComponent} from "@app/_components/table-pager/table-pager.component";

interface RegenerateExtractForm {
    extractKey: FormControl<string | null>;
    startDate: FormControl<NgbDate | null>;
    endDate: FormControl<NgbDate | null>;
}

@Component({
    selector: 'app-extracts',
    imports: [
        TablePagerComponent,
        AsyncPipe,
        FormsModule,
        ReactiveFormsModule,
        SortableDirective,
        ModalConfirmComponent,
        NgbInputDatepicker
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './extracts.component.html'
})
export class ExtractsComponent implements OnDestroy {
    @ViewChildren(SortableDirective) headers!: QueryList<SortableDirective>;
    @ViewChild('confirmationModal') modalConfirmComponent!: ModalConfirmComponent;
    @ViewChild('regenerateExtractModal') private regenerateExtractModal!: TemplateRef<ModalConfirmComponent>;

    customers: Customer[] = [];
    customerCode: string | undefined;
    extractKeys: string[] = [];
    savingData: boolean = false;
    today = inject(NgbCalendar).getToday();

    regenerateExtractForm = new FormGroup<RegenerateExtractForm>({
        extractKey: new FormControl(null, Validators.required),
        startDate: new FormControl(this.today),
        endDate: new FormControl(this.today),
    });

    protected readonly formatDate = formatDate;
    protected readonly filesize = filesize;

    private onRegenerateExtractModalRef!: NgbModalRef
    private _customers$ = new BehaviorSubject<Customer[]>([]);
    private _extractKeys$ = new BehaviorSubject<string[]>([]);
    private customersSubscription: Subscription;
    private extractKeysSubscription: Subscription;
    private selectedCustomer: Customer | undefined;

    public constructor(public globalService: GlobalService, private apiService: ApiService, protected extractsService: ExtractsService, private route: ActivatedRoute, private toastService: ToastService, private modalService: NgbModal) {
        this._customers$.next(this.route.snapshot.data['resolvedData'].data);
        this.customersSubscription = this._customers$.subscribe(customers => {
            this.customers = customers;
        });

        this.extractKeysSubscription = this._extractKeys$.subscribe(extractKeys => {
            this.extractKeys = extractKeys;
        });
    }

    get controls() {
        return this.regenerateExtractForm.controls;
    }

    ngOnDestroy() {
        this.customersSubscription.unsubscribe();
        this.extractKeysSubscription.unsubscribe();
    }

    onCustomerChange(event: Event) {
        this.customerCode = (event.target as HTMLInputElement).value;
        this.selectedCustomer = this.customers.find(customer => customer.code === this.customerCode);

        if (!this.customerCode) {
            return;
        }

        this.globalService.showWaitOverlay();

        this.apiService.getExtractsByCustomerCode(this.customerCode).subscribe({
            next: (response: VineMedsS3ObjectVersion[]) => {
                this.extractsService.data = response;
                this.extractsService.refreshDataSource();
                this.globalService.hideWaitOverlay();
            }
        });

        this.apiService.getExtractKeysByCustomerCode(this.customerCode).subscribe({
            next: (response: string[]) => {
                this._extractKeys$.next(response);
            }
        });
    }

    onSort({column, direction}: SortEvent) {
        // resetting other headers
        for (const header of this.headers) {
            if (header.sortable !== column) {
                header.direction = '';
            }
        }

        this.extractsService.sortColumn = column;
        this.extractsService.sortDirection = direction;
    }

    onDownloadFile(key: string, versionId: string): void {
        let customerId = this.selectedCustomer?.id!;
        this.extractsService.downloadRawFile(customerId, key, versionId);
    }

    showDownloadButton(key: string): boolean {
        return !key.trimEnd().endsWith('/');
    }

    onRegenerateExtract() {
        this.onRegenerateExtractModalRef = this.modalService.open(this.regenerateExtractModal, {
            modalDialogClass: "modal-dialog-centered",
            backdrop: "static",
        });
    }

    onRegenerateExtractDismiss() {
        this.modalConfirmComponent.confirmIfDirty(this.regenerateExtractForm, () => this.onRegenerateExtractModalRef.dismiss());
    }

    onRegenerateExtractSubmit() {
        if (this.regenerateExtractForm.invalid) {
            this.toastService.showDanger("Invalid Data", "Please fill-in all required fields.");
            return;
        }

        this.savingData = true;
        const extract = this.regenerateExtractForm.value;
        const extractRequest: RegenerateExtractRequest = {
            extractKey: extract.extractKey!,
            startDate: this.formatNgbDateAsString(extract.startDate!),
            endDate: this.formatNgbDateAsString(extract.endDate!)
        };

        this.apiService.regenerateExtractByKey(extractRequest).subscribe({
            next: (response: RegenerateExtractResponse) => {
                this.toastService.showSuccess("Regenerate Extract", response.message);
            },
            error: (e) => {
                this.toastService.showDanger("Request Failed", "Please try again.");
                this.savingData = false;
                throw e;
            },
            complete: () => {
                this.savingData = false;
                this.onRegenerateExtractModalRef.close();
            }
        });
    }

    private formatNgbDateAsString(input: NgbDate | null) : string | null {
        if (!input) {
            return null;
        }
        return `${input.year}-${this.getPaddedNumber(input.month)}-${this.getPaddedNumber(input.day)}`;
    }

    private getPaddedNumber(input: number): string {
        return input.toString().padStart(2,"0");
    }
}
